// Teams (org-scoped groups of members). Admin-only.
//
// A team is a named bag of users. Teams are referenced from:
//   - Escalation policy steps with target type 'team' — fan-out to all
//     members at fire time
//   - Schedules — a schedule can optionally be bound to a team
//
// Empty teams are allowed; admins often want to create a "Senior
// Managers" team first then populate later. Same pattern as schedules
// — inline create + edit per row.

import { useEffect, useMemo, useState } from 'react';
import { onSnapshot, orderBy, query } from 'firebase/firestore';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgMembersCol, orgTeamsCol } from '../lib/firestore';
import { createTeam, deleteTeam, updateTeam } from '../lib/teams';
import type { OrgMember, Team } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function TeamsPage() {
  const { orgId } = useOrg();
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const unsubTeams = onSnapshot(
      query(orgTeamsCol(orgId), orderBy('createdAt', 'asc')),
      (snap) => setTeams(snap.docs.map((d) => d.data() as Team)),
      (err) => {
        console.error('[teams] snapshot failed', err);
        setError('Could not load teams.');
      },
    );
    const unsubMembers = onSnapshot(
      query(orgMembersCol(orgId), orderBy('joinedAt', 'asc')),
      (snap) => setMembers(snap.docs.map((d) => d.data() as OrgMember)),
    );
    return () => {
      unsubTeams();
      unsubMembers();
    };
  }, [orgId]);

  async function onDelete() {
    if (!confirmDeleteId || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteTeam(orgId!, confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('[teams] delete failed', err);
      setError(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  if (!orgId) return null;

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="Teams"
          subtitle="Groups of org members. Reference a team from an escalation policy step (fans out to every member at fire time) or bind a team to an on-call rotation."
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          {showForm ? (
            <TeamForm
              orgId={orgId}
              members={members}
              onCancel={() => setShowForm(false)}
              onSaved={() => setShowForm(false)}
              onError={(m) => setError(m)}
            />
          ) : (
            <Row justify="end">
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Plus size={12} strokeWidth={2} />}
                onClick={() => setShowForm(true)}
              >
                New team
              </Button>
            </Row>
          )}

          {teams === null ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          ) : teams.length === 0 ? (
            <Card>
              <CardBody>
                <Row gap={3} align="center">
                  <Users size={20} strokeWidth={1.75} color="var(--color-text-tertiary)" />
                  <Stack gap={1}>
                    <span style={{ fontWeight: 'var(--fw-medium)' }}>No teams yet</span>
                    <span className="ds-text-muted ds-text-sm">
                      Create your first team, then reference it from an escalation policy
                      step or bind it to an on-call rotation.
                    </span>
                  </Stack>
                </Row>
              </CardBody>
            </Card>
          ) : (
            <Stack gap={3}>
              {teams.map((t) => (
                <TeamRow
                  key={t.id}
                  team={t}
                  orgId={orgId}
                  members={members}
                  onDelete={() => setConfirmDeleteId(t.id)}
                  onError={(m) => setError(m)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <ConfirmDialog
          open={confirmDeleteId !== null}
          onConfirm={onDelete}
          onCancel={() => setConfirmDeleteId(null)}
          title="Delete this team?"
          confirmLabel="Delete"
          destructive
          loading={deleting}
        >
          <p>
            Any escalation policy step or schedule that references this team will fail to
            route at trigger time. Update those references before deleting.
          </p>
        </ConfirmDialog>
      </Container>
    </AppShell>
  );
}

function TeamRow({
  team,
  orgId,
  members,
  onDelete,
  onError,
}: {
  team: Team;
  orgId: string;
  members: OrgMember[];
  onDelete: () => void;
  onError: (m: string) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <Card>
        <CardBody>
          <TeamForm
            orgId={orgId}
            members={members}
            editing={team}
            onCancel={() => setEditing(false)}
            onSaved={() => setEditing(false)}
            onError={onError}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <Stack gap={3}>
          <Row gap={3} justify="between" align="start">
            <Stack gap={1}>
              <Row gap={2} align="center">
                <Users size={14} strokeWidth={2} color="var(--color-accent)" />
                <span style={{ fontWeight: 'var(--fw-semibold)' }}>{team.name}</span>
              </Row>
              <span className="ds-text-muted ds-text-sm">
                {team.memberIds.length}{' '}
                {team.memberIds.length === 1 ? 'member' : 'members'}
              </span>
            </Stack>
            <Row gap={1} align="center">
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Pencil size={12} strokeWidth={2} />}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              <Button
                variant="danger-ghost"
                size="sm"
                iconLeft={<Trash2 size={12} strokeWidth={2} />}
                onClick={onDelete}
              >
                Delete
              </Button>
            </Row>
          </Row>

          {team.memberIds.length > 0 ? (
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-1)',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {team.memberIds.map((uid) => (
                <Badge key={uid} tone="neutral">
                  {team.memberDisplayNames?.[uid] ?? uid}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="ds-text-tertiary ds-text-sm">
              No members yet — edit the team to add some.
            </span>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

function TeamForm({
  orgId,
  members,
  editing,
  onCancel,
  onSaved,
  onError,
}: {
  orgId: string;
  members: OrgMember[];
  editing?: Team;
  onCancel: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const isEdit = Boolean(editing);
  const [name, setName] = useState(editing?.name ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(editing?.memberIds ?? []),
  );
  const [submitting, setSubmitting] = useState(false);

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [members],
  );

  const canSubmit = name.trim().length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const memberIds = Array.from(selected);
      if (isEdit && editing) {
        await updateTeam(orgId, editing.id, { name: name.trim(), memberIds });
      } else {
        await createTeam(orgId, { name: name.trim(), memberIds });
      }
      onSaved();
      if (!isEdit) {
        setName('');
        setSelected(new Set());
      }
    } catch (err) {
      console.error('[teams] save failed', err);
      onError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function toggleMember(uid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }

  return (
    <Stack gap={4} as="form" onSubmit={submit}>
      <Row gap={2} align="center">
        <Users size={14} strokeWidth={2} color="var(--color-accent)" />
        <span style={{ fontWeight: 'var(--fw-medium)' }}>
          {isEdit ? 'Edit team' : 'New team'}
        </span>
      </Row>

      <FormField label="Name" hint="What this team covers — e.g. 'Platform', 'Senior managers'.">
        {(id) => (
          <Input
            id={id}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Platform"
            maxLength={100}
            autoFocus
            required
          />
        )}
      </FormField>

      <Stack gap={2}>
        <span className="ds-label">Members ({selected.size} selected)</span>
        {sortedMembers.length === 0 ? (
          <span className="ds-text-tertiary ds-text-sm">No org members to add yet.</span>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-1)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {sortedMembers.map((m) => {
              const isSelected = selected.has(m.userId);
              return (
                <Button
                  key={m.userId}
                  type="button"
                  variant={isSelected ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => toggleMember(m.userId)}
                >
                  {m.displayName ?? m.email ?? m.userId}
                </Button>
              );
            })}
          </div>
        )}
      </Stack>

      <Row gap={2} justify="end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={!canSubmit} loading={submitting}>
          {isEdit ? 'Save changes' : 'Create team'}
        </Button>
      </Row>
    </Stack>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
